import { action } from "@storybook/addon-actions";
import type { Meta, StoryFn } from "@storybook/react";
import React from "react";
import { ArrowDownIcon, ArrowDownUpIcon, ArrowUpIcon, ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import { Menu } from ".";
import Flex from "../Flex";
import { Button } from "../Button";

export default {
    component: Menu,
    title: "common/Menu"
} as Meta<typeof Menu>;

/**
 * The "Default" story represents the most basic usage of the Menu component.
 * It provides a simple way to display a menu triggered by a button, where the menu items do not have any grouped sections.
 * This story is useful for understanding the default behavior and structure of the Menu component.
 */
export const Default: StoryFn = () => (
    <Menu
        label={({ state, props, ref }) => (
            <Button
                {...props}
                title="Add order"
                trackName=""
                ref={ref}
                endIcon={state.isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
            />
        )}
        onAction={action("onAction")}
    >
        <Menu.Item key="b2b">B2B order</Menu.Item>
        <Menu.Item key="b2c">B2C order</Menu.Item>
        <Menu.Item key="b2b-bulk">B2B bulk import</Menu.Item>
        <Menu.Item key="b2c-bulk">B2C bulk import</Menu.Item>
    </Menu>
);

/**
 * The "WithSection" story enhances the Menu component by introducing sections within the menu.
 * This allows for better organization of menu items, especially when there are logical groupings that can help users navigate the options more intuitively.
 * This example demonstrates how to implement and style sections within the Menu component.
 */
export const WithSection: StoryFn = () => (
    <Menu
        label={({ state, props, ref }) => (
            <Button
                {...props}
                title="Add order"
                trackName=""
                ref={ref}
                endIcon={state.isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
            />
        )}
        onAction={action("onAction")}
    >
        <Menu.Item key="b2b">B2B order</Menu.Item>
        <Menu.Item key="b2c">B2C order</Menu.Item>
        <Menu.Section title="Bulk import">
            <Menu.Item key="b2b-bulk">B2B bulk import</Menu.Item>
            <Menu.Item key="b2c-bulk">B2C bulk import</Menu.Item>
        </Menu.Section>
    </Menu>
);

/**
 * The "WithSelection" story showcases the Menu component's ability to handle item selection.
 * This variant of the Menu allows for single item selection, reflecting the chosen option in the triggering button's title.
 * It is a practical example of how to manage state for selection and update the UI in response to user interactions.
 */
export const WithSelection: StoryFn = () => {
    const [selection, setSelection] = React.useState<string[]>([]);

    return (
        <Menu
            selectionMode="single"
            type="listbox"
            selectedKeys={selection}
            onSelectionChange={newSelection => {
                const value = (newSelection as any).currentKey as string | undefined;

                setSelection(value ? [value] : []);
            }}
            label={({ state, props, ref }) => (
                <Button
                    {...props}
                    title={selection.length > 0 ? `Select (${selection[0]})` : "Select"}
                    trackName=""
                    ref={ref}
                    endIcon={state.isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                />
            )}
        >
            <Menu.Item textValue="asc" key="asc">
                <Flex gap={8}>
                    <ArrowUpIcon size={16} />
                    <span>Ascending</span>
                </Flex>
            </Menu.Item>
            <Menu.Item textValue="desc" key="desc">
                <Flex gap={8}>
                    <ArrowDownIcon size={16} />
                    <span>Descending</span>
                </Flex>
            </Menu.Item>
            <Menu.Item textValue="none" key="none">
                <Flex gap={8}>
                    <ArrowDownUpIcon size={16} />
                    <span>None</span>
                </Flex>
            </Menu.Item>
        </Menu>
    );
};
